import React, { useCallback, useContext, useState } from 'react';
import usePopup from '../hooks/usePopup';
import CardContext from 'contexts/CardContext';
import DashboardContextMenuContext from 'contexts/DashboardContextMenuContext';
import DashboardGadgetContext from 'contexts/DashboardGadgetContext';
import { InsightsChatContext } from 'contextProviders/InsightsChatProvider';
import useExplainValue from 'components/InsightsChat/explainValue/useExplainValue';
import useGadgetDrillTarget from 'components/InsightsChat/explainValue/useGadgetDrillTarget';
import { useCompositeChartGadget } from 'components/Studio/CompositeCard/compositeChartPopup';
import DashboardContextMenu from '../components/DashboardContextMenu';

const DashboardContextMenuProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const [metricIds, setMetricIds] = useState<string[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<
    Metrics.NormalMetric | Metrics.SpecialMetric | undefined
  >();
  const [drillDown, setDrillDown] = useState<FixedFilter | undefined>();
  const [queryOverrides, setQueryOverrides] = useState<
    V5SeriesQueryOverrides | undefined
  >();
  const { isOpen, open, close } = usePopup();

  // Insights "pick a value to explain": a dashboard chart's click never reaches
  // the drill-down builders (openMenu pre-empts onDrillDown in the chart option
  // files), so the armed picker is honoured here instead of showing the menu.
  // This provider is mounted per card inside DashboardGadgetProvider, so the
  // card id and gadget are in scope. A Composite's chart has no gadget, and
  // mounts it per chart under its own card context, whose chart stands in.
  const { explainPicker } = useContext(InsightsChatContext);
  const { isAvailable: isExplainAvailable, explain } = useExplainValue();
  const { i: cardId, chartDefinition } = useContext(CardContext);
  const { dashboardGadget } = useContext(DashboardGadgetContext);
  const compositeChartGadget = useCompositeChartGadget();
  const drillTarget = useGadgetDrillTarget(
    dashboardGadget ?? compositeChartGadget,
  );
  const isPickerArmed = explainPicker.isArmed && isExplainAvailable;

  const openMenu = useCallback(
    (
      mIds: string[],
      drill: FixedFilter,
      overrides?: V5SeriesQueryOverrides,
    ) => {
      if (isPickerArmed) {
        const chartDef = dashboardGadget?.chartDef ?? chartDefinition;
        explain({
          drillDown: drill,
          metricIds: mIds,
          chartDef,
          cardId,
          gadgetTitle: chartDef?.name ?? '',
          drillTarget,
          source: 'picker',
        });
        return;
      }
      setMetricIds(mIds);
      setDrillDown(drill);
      setQueryOverrides(overrides);
      open();
    },
    [
      open,
      isPickerArmed,
      explain,
      dashboardGadget,
      chartDefinition,
      cardId,
      drillTarget,
    ],
  );

  const closeMenu = useCallback(() => {
    setMetricIds([]);
    setSelectedMetric(undefined);
    setDrillDown(undefined);
    setQueryOverrides(undefined);
    close();
  }, [close]);

  return (
    <DashboardContextMenuContext.Provider
      value={{
        isOpen,
        drillDown,
        queryOverrides,
        openMenu,
        closeMenu,
        metricIds,
        selectedMetric,
        setSelectedMetric,
      }}
    >
      <React.Fragment>
        {children}
        <DashboardContextMenu />
      </React.Fragment>
    </DashboardContextMenuContext.Provider>
  );
};

export default DashboardContextMenuProvider;
